import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

interface SiteHeaderProps {
  overlay?: boolean;
}

const navItems = [
  { to: '/', label: '首页' },
  { to: '/frontline', label: '围观前线' },
  { to: '/squad', label: '吃瓜小队' },
  { to: '/about', label: '关于我们' },
];

export default function SiteHeader({ overlay = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const solid = !overlay || scrolled;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        solid
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2.5 group">
            <span className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-seedling-line text-lg md:text-xl"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={`font-heading font-bold text-lg md:text-xl tracking-tight ${
                  solid ? 'text-foreground-950' : 'text-background-50'
                }`}
              >
                511群众
              </span>
              <span
                className={`text-[10px] md:text-xs mt-0.5 tracking-wider ${
                  solid ? 'text-foreground-500' : 'text-background-100'
                }`}
              >
                抱团吃瓜 · 围观前线
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-100 text-primary-800'
                      : solid
                        ? 'text-foreground-700 hover:text-primary-700 hover:bg-background-100'
                        : 'text-background-100 hover:text-background-50'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/frontline"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-foreground-950 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap"
            >
              <i className="ri-radar-line"></i>
              进前线
            </Link>
          </div>

          <button
            type="button"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md ${
              solid ? 'text-foreground-900 bg-background-100' : 'text-background-50 bg-white/15'
            }`}
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50">
          <nav className="flex flex-col px-4 py-3 gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-md text-sm font-medium ${
                    isActive ? 'bg-primary-100 text-primary-800' : 'text-foreground-700'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/frontline"
              className="mt-2 px-4 py-3 rounded-md bg-foreground-950 text-background-50 text-sm font-medium text-center"
            >
              进入围观前线
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}